import React, { useCallback, useMemo, useState } from 'react';
import { useObserverReturningStore, uuid } from '../../../CodesThatsGottaGoSomewhereElse';
import { ObjectManager } from '../../../concepts/objects';
import { Colors, Input, Menu } from '../../../ui';
import { PickerMenu } from '../../../ui/PickerMenu';
import { FlexScroller } from '../../../ui/Scrollers';
import { useResolve } from '../../Container';
import { ProfileModel } from '../../ProfileModel';
import { Physical } from '../../canvas/entities/Physical';
import { TextSettings } from '../../canvas/entities/TextConventions';
import { PropertyFacade } from '../../canvas/properties/PropertyFacade';
import { PopoutWidget } from '../../canvas/workspace/widgets/WidgetInterface';
import { Copy, Flexer, Title } from '../../dashboard/elements/LayoutElements';
import { AtsertState, useAtsertDongle } from '../annotations/CommentAtsertView';
import { QuillInput } from '../mentions/QuillInput';
import { TextGenerator } from './TextGenerator';

const useBoardObjects = (search: string) => {
    const objects = useResolve(ObjectManager);
    return objects.findAll(Physical, x => x?.toText()?.includes(search));
};

type SelectBoardObjectViewProps = {
    title?: string,
    search?: string,
    allowKeyboard?: boolean,
    allowSearch?: boolean,
    onSelect: (elmt: Physical) => void,
};

const useRenderItem = () => {
    return useMemo(() =>
        (item: Physical, index: number) => <>
            {/* <Avatar.ForUser size="1.5" user={item} /> */}
            <Menu.ItemTextCropped>{item?.toText() || item.type}</Menu.ItemTextCropped>
            <Menu.Shortcut keys={item.type} />
        </>,
        [],
    );
};

const SelectBoardObjectView = ({ title, search, allowKeyboard, allowSearch, onSelect }: SelectBoardObjectViewProps) => {
    const [searchInput, setSearchInput] = useState('');
    search = search ?? searchInput;

    const list = useBoardObjects(search);

    const onSelectFromMenu = useCallback(({ id }) => {
        onSelect(list.find(x => x.id === id));
    }, [onSelect, list]);

    const renderItem = useRenderItem();

    return <Flexer flex="column" width="18" height="16.25">
        {title &&
            <Flexer padding=".5 1" background={Colors.gray50} borderRadius=".5rem .5rem 0 0" borderBottom={`1px solid ${Colors.borderLight}`}>
                <Title flush size="2xs" color={Colors.text} uppercase weight="500">{title}</Title>
            </Flexer>
        }
        {allowSearch &&
            <Flexer padding=".5">
                <Input autoFocus compact placeholder="Search..." value={search} onChange={setSearchInput} />
            </Flexer>
        }
        <FlexScroller>
            <PickerMenu allowKeyboard={allowKeyboard} items={list} renderItem={renderItem} onAction={onSelectFromMenu} />
            {!!search && list.length === 0 &&
                <Flexer grow flex center>
                    <Copy flush>Sorry, no matches!</Copy>
                </Flexer>
            }
        </FlexScroller>
        <Flexer height=".5" />
    </Flexer>;
};

const AtsertView = ({ open, at, query, onAction }: AtsertState) => {
    if (!open) return null;

    const me = useResolve(ProfileModel);

    const onSelect = (elmt: Physical) => {
        onAction('insert', {
            fromId: me.id,
            toId: elmt.id,
            value: elmt?.toText() || elmt.type,
            tag: uuid('nodash').slice(0, 8),
        });
    };

    const style = {
        ...at.toCSS(),
        width: '18rem',
        height: '16.25rem',
        background: 'white',
        borderRadius: '.5rem',
        boxShadow: `0 0 0 1px ${Colors.border}`,
    };
    return <Flexer absolute style={style}>
        <SelectBoardObjectView title="Elements:" allowKeyboard search={query} onSelect={onSelect} />
    </Flexer>;
};

export const TextGeneratorPromptWidget = ({ widget }: { widget: PopoutWidget }) => {

    const objects = useResolve(ObjectManager);
    const facade = widget.facade as PropertyFacade<TextGenerator>;
    const [value, setValue] = useState(facade.get('prompt') || '');

    const onChange = useCallback((next: string) => {
        objects.update(u => {
            u.rolling(facade.keyFor('prompt'));
            facade.set('prompt', next);
        });
    }, [value, facade]);

    const atsert = useObserverReturningStore(useAtsertDongle());

    return <Flexer padding="0" width="24" height="16" >
        <QuillInput
            atsert={atsert}
            autoFocus
            // enterMode="commit"
            formatting={['mention']}
            initialValue={value}
            onChange={onChange}
            placeholder="Write the generator prompt here..."
            textSettings={TextSettings.default}
        />
        <AtsertView {...atsert.getState()} />
    </Flexer>;
};
